/* ──────────────────────────────────────────────────────────────
 *  GeometryViewer — 3D Plotly Mesh3d + Scatter3d edges component
 *
 *  Renders tessellated mesh data received from the geometry API.
 *  Uses orthographic camera with:
 *    - Mesh3d for solid component surfaces
 *    - Scatter3d for CAD edge wireframe overlay
 *
 *  The geometry-view FlipButton is rendered as an overlay pinned
 *  to the bottom-right corner of this white panel. Only its
 *  position changed — sequencing/disable logic is unchanged and
 *  still fully owned by the parent (GeometryPanel) via onFlip /
 *  flipDisabled / currentView.
 *
 *  Component colors follow the Jupyter app:
 *    - MRE/Solid: #666666 (dark gray)
 *    - Core: #D0D0D0 (light gray) or #73B6D7 (steel blue)
 *    - Outer: #D0D0D0 (light gray) or #73B6D7 (steel blue)
 * ────────────────────────────────────────────────────────────── */

import React, { useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Plot from 'react-plotly.js';
import type { Layout, ModeBarDefaultButtons } from 'plotly.js';
import type { GeometryMesh, GeometryView } from '@/types/geometry';

import FlipButton from './FlipButton';

interface GeometryViewerProps {
  mesh: GeometryMesh | undefined;
  viewType: GeometryView;
  isLoading: boolean;
  /** True when fetching new data but placeholder (previous) data is shown. */
  isFetching: boolean;
  isError: boolean;

  /** Currently displayed geometry view (for the FlipButton icon/tooltip). */
  currentView: GeometryView;
  /** Whether geometry view switching is disabled. */
  flipDisabled: boolean;
  /** Handler responsible for advancing to the next view. */
  onFlip: () => void;
}

/** Build Plotly traces from the geometry mesh response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildTraces(mesh: GeometryMesh): any[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const traces: any[] = [];

  for (const component of mesh.components) {
    const { vertices, triangles, edges, color, label } = component;

    // Parse flat vertex array into x, y, z arrays
    const vertexCount = vertices.length / 3;
    const x = new Array<number>(vertexCount);
    const y = new Array<number>(vertexCount);
    const z = new Array<number>(vertexCount);
    for (let idx = 0; idx < vertexCount; idx++) {
      x[idx] = vertices[idx * 3];
      y[idx] = vertices[idx * 3 + 1];
      z[idx] = vertices[idx * 3 + 2];
    }

    // Parse flat triangle index array into i, j, k arrays
    const triCount = triangles.length / 3;
    const i = new Array<number>(triCount);
    const j = new Array<number>(triCount);
    const k = new Array<number>(triCount);
    for (let idx = 0; idx < triCount; idx++) {
      i[idx] = triangles[idx * 3];
      j[idx] = triangles[idx * 3 + 1];
      k[idx] = triangles[idx * 3 + 2];
    }

    // Mesh3d surface trace
    traces.push({
      type: 'mesh3d',
      x,
      y,
      z,
      i,
      j,
      k,
      color,
      flatshading: false,
      opacity: 1.0,
      lighting: {
        ambient: 1.0,
        diffuse: 0.0,
        specular: 0.0,
        roughness: 0.0,
        fresnel: 0.0,
      },
      name: label,
      showlegend: false,
      hoverinfo: 'x+y+z',
      hovertemplate: 'x: %{x}<br>y: %{y}<br>z: %{z}<extra>' + label + '</extra>',
    });

    // Scatter3d edge wireframe overlay
    if (edges.x.length > 0) {
      traces.push({
        type: 'scatter3d',
        x: edges.x,
        y: edges.y,
        z: edges.z,
        mode: 'lines',
        line: { color: '#000000', width: 2 },
        opacity: 1.0,
        hoverinfo: 'skip',
        showlegend: false,
        name: `${label} edges`,
      });
    }
  }

  return traces;
}

/**
 * Compute a bounding box from the first component's flat vertex array.
 * Mirrors the legacy Python `_bb()` which uses the first component's OCP
 * BoundingBox — tessellated vertices approximate this closely.
 */
function computeBoundingBox(mesh: GeometryMesh) {
  // Use first component (MRE / Solid) to match legacy bounding_shape logic
  const verts = mesh.components[0]?.vertices;
  if (!verts || verts.length < 3) return null;

  let xmin = Infinity, xmax = -Infinity;
  let ymin = Infinity, ymax = -Infinity;
  let zmin = Infinity, zmax = -Infinity;

  const count = verts.length / 3;
  for (let i = 0; i < count; i++) {
    const vx = verts[i * 3];
    const vy = verts[i * 3 + 1];
    const vz = verts[i * 3 + 2];
    if (vx < xmin) xmin = vx; if (vx > xmax) xmax = vx;
    if (vy < ymin) ymin = vy; if (vy > ymax) ymax = vy;
    if (vz < zmin) zmin = vz; if (vz > zmax) zmax = vz;
  }

  return {
    xmin, xmax, ymin, ymax, zmin, zmax,
    xlen: xmax - xmin,
    ylen: ymax - ymin,
    zlen: zmax - zmin,
  };
}

/** Build the Plotly layout for the 3D scene.
 *
 *  Camera placement matches the legacy Jupyter app exactly:
 *    - ISO:  eye at (+d, +d, +d), up = Z, orthographic, d = 1.5 × maxdim
 *    - SXH/AXH: eye at (0, 0, −2d), up = Y, orthographic, d = 2.0 × maxdim
 *  Axis ranges are set to bb ± 8 % padding (same pad_ratio as legacy).
 */
function buildLayout(viewType: GeometryView, mesh?: GeometryMesh): Partial<Layout> {
  const PAD_RATIO = 0.08;

  const bb = mesh ? computeBoundingBox(mesh) : null;

  // Compute camera & axis ranges when bounding box is available
  let camera: Record<string, unknown> = {};
  let axisRanges: { xr?: [number, number]; yr?: [number, number]; zr?: [number, number] } = {};

  if (bb) {
    const maxdim = Math.max(bb.xlen, bb.ylen, bb.zlen);
    const pad = PAD_RATIO * maxdim;
    axisRanges = {
      xr: [bb.xmin - pad, bb.xmax + pad],
      yr: [bb.ymin - pad, bb.ymax + pad],
      zr: [bb.zmin - pad, bb.zmax + pad],
    };

    if (viewType === 'iso') {
      const d = 1.5 * maxdim;
      camera = {
        eye: { x: d, y: d, z: d },
        up: { x: 0, y: 0, z: 1 },
        center: { x: 0, y: 0, z: 0 },
        projection: { type: 'orthographic' },
      };
    } else {
      // sxh / axh — look straight at the cross-section face (front=false → −Z)
      const d = 2.0 * maxdim;
      camera = {
        eye: { x: 0, y: 0, z: -d },
        up: { x: 0, y: 1, z: 0 },
        center: { x: 0, y: 0, z: 0 },
        projection: { type: 'orthographic' },
      };
    }
  } else {
    // Fallback when mesh is not yet available
    if (viewType !== 'iso') {
      camera = { projection: { type: 'orthographic' } };
    }
  }

  const makeAxis = (range?: [number, number]) => ({
    title: { text: '' },
    showticklabels: false,
    ticks: '' as const,
    showgrid: false,
    zeroline: false,
    showline: false,
    showbackground: false,
    backgroundcolor: 'white',
    ...(range ? { range } : {}),
  });

  return {
    autosize: true,
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    margin: { l: 0, r: 0, t: 0, b: 0 },
    showlegend: false,
    scene: {
      xaxis: makeAxis(axisRanges.xr),
      yaxis: makeAxis(axisRanges.yr),
      zaxis: makeAxis(axisRanges.zr),
      bgcolor: 'white',
      camera,
      aspectmode: 'data',
    },
  } as Partial<Layout>;
}

const GeometryViewer: React.FC<GeometryViewerProps> = ({
  mesh,
  viewType,
  isLoading,
  isFetching,
  isError,
  currentView,
  flipDisabled,
  onFlip,
}) => {
  const traces = useMemo(() => (mesh ? buildTraces(mesh) : []), [mesh]);
  const layout = useMemo(() => buildLayout(viewType, mesh), [viewType, mesh]);

  // Stable config reference — prevents Plotly from re-creating the WebGL context
  const configRef = useRef({
    displayModeBar: true,
    modeBarButtonsToRemove: ['toImage', 'sendDataToCloud'] as ModeBarDefaultButtons[],
    displaylogo: false,
    scrollZoom: true,
    responsive: true,
  });
  const plotConfig = configRef.current;

  // Stable style reference — prevents unnecessary DOM updates
  const styleRef = useRef({ width: '100%', height: '100%' });
  const plotStyle = styleRef.current;

  /*
   * The flip control now lives inside this white panel, pinned to the
   * bottom-right corner, on top of whichever state (empty/loading/error/
   * loaded) is currently rendered below.
   */
  const flipOverlay = (
    <Box
      sx={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        zIndex: 2,
      }}
    >
      <FlipButton
        currentView={currentView}
        disabled={flipDisabled}
        onFlip={onFlip}
      />
    </Box>
  );

  // Empty state — no sample selected or no data
  if (!mesh && !isLoading && !isError) {
    return (
      <Box
        sx={{
          flex: 1,
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <ViewInArIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.2 }} />
        <Typography variant="body2" color="text.secondary">
          Select a generated sample to view geometry
        </Typography>
        {flipOverlay}
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading 3D geometry...
        </Typography>
        {flipOverlay}
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box
        sx={{
          flex: 1,
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <ViewInArIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.4 }} />
        <Typography variant="body2" color="error">
          Failed to load geometry
        </Typography>
        {flipOverlay}
      </Box>
    );
  }

  // Render Plotly 3D viewer
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: '14px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        minHeight: 0,
        position: 'relative',
      }}
    >
      <Plot
        data={traces}
        layout={layout}
        config={plotConfig}
        useResizeHandler
        style={plotStyle}
        // Use revision-based updates — only re-renders when data actually changes
        revision={traces.length}
      />
      {/* Subtle opacity overlay while fetching new mesh (previous mesh still visible) */}
      {isFetching && mesh && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255,255,255,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={20} sx={{ opacity: 0.6 }} />
        </Box>
      )}
      {flipOverlay}
    </Box>
  );
};

export default React.memo(GeometryViewer);